import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import selectors from '../redux/selectors/selectors';

const tooltipDelay = 200;
const tooltipPadding = 6;

const getTitledElement = (startElement) => {
  const iterate = (element) => {
    if (element && typeof element.hasAttribute === 'function' && element.hasAttribute('data-title')) {
      return element;
    }
    if (element.parentElement && typeof element.parentElement.hasAttribute === 'function' && !['BODY', 'HTML'].includes(element.nodeName)) {
      return iterate(element.parentElement);
    }
    return undefined;
  };
  return iterate(startElement);
};

const TooltipHandler = () => {
  const state = useRef({});
  const isMobile = useSelector(selectors.mobile);

  const tooltipIntervalHandler = () => {
    if (state.current.element && !state.current.tooltip) {
      const hovered = state.current.element;
      const newTooltipDelay = hovered.getAttribute('data-title-delay') || tooltipDelay;
      const delay = new Date() - state.current.duration;
      if (delay < newTooltipDelay) {
        return;
      }

      createTooltip(hovered);
    }
  };

  const createTooltip = (hovered) => {
    const tooltip = document.createElement('div');
    tooltip.classList.add('tooltip-component');
    const tooltipText = document.createElement('div');
    const title = hovered.getAttribute('data-title');
    const shortcut = hovered.getAttribute('data-title-shortcut');

    if (!title) {
      return;
    }

    tooltipText.innerHTML = title.split('\n').join('<br>');
    if (shortcut) {
      const shortcutSpan = document.createElement('kbd');
      shortcutSpan.classList.add('comp-kbd');
      shortcutSpan.innerHTML = shortcut;
      tooltipText.appendChild(shortcutSpan);
    }

    const offsetY = hovered.getAttribute('data-title-offset-y');
    tooltip.appendChild(tooltipText);
    document.body.appendChild(tooltip);

    const viewportOffset = hovered.getBoundingClientRect();
    const elementWidth = hovered.offsetWidth;
    const elementHeight = hovered.offsetHeight;
    const tooltipWidth = tooltip.offsetWidth;
    const tooltipHeight = tooltip.offsetHeight;
    const top = viewportOffset.top;
    const left = viewportOffset.left;

    const regularTop = top + elementHeight + tooltipPadding;
    const invertedTop = top - tooltipPadding - tooltipHeight;

    const regularLeft = left + elementWidth / 2 - tooltipWidth / 2;
    const leftAlignLeft = left + elementWidth / 2 - 16;
    const rightAlignLeft = left + elementWidth - tooltipWidth;

    const invertY = regularTop + tooltipHeight > window.innerHeight;
    if (invertY) {
      tooltip.classList.add('inverted-y');
    }

    const leftAlign = regularLeft <= 0;
    if (leftAlign) {
      tooltip.classList.add('left-align');
    }

    const rightAlign = regularLeft + tooltipWidth >= document.body.clientWidth;
    if (rightAlign) {
      tooltip.classList.add('right-align');
    }

    let finalTop = invertY ? invertedTop : regularTop;
    const finalLeft = leftAlign ? leftAlignLeft : rightAlign ? rightAlignLeft : regularLeft;

    if (offsetY) {
      finalTop = finalTop + (invertY ? -offsetY : offsetY);
    }

    tooltip.style.top = `${finalTop}px`;
    tooltip.style.left = `${finalLeft}px`;

    state.current.tooltip = tooltip;

    setTimeout(() => {
      tooltip.classList.add('active');
    });
  };

  const isInsideTooltip = (node) => {
    return node && typeof node.closest === 'function' && node.closest('.tooltip-component');
  };

  const hideTooltip = (e) => {
    state.current.duration = undefined;
    state.current.element = undefined;
    if (state.current.tooltip) {
      if (e) {
        const currentTitledElement = getTitledElement(e.target) || getTitledElement(e.relatedTarget || {});
        const isHoveringTooltip = isInsideTooltip(e.target) || isInsideTooltip(e.relatedTarget || {});
        if (currentTitledElement || isHoveringTooltip) {
          return;
        }
      }

      const tooltip = state.current.tooltip;
      state.current.tooltip = undefined;
      if (window.debugTooltip !== true) {
        tooltip.classList.remove('active');
      }
      setTimeout(() => {
        if (tooltip) {
          if (window.debugTooltip !== true) {
            document.body.removeChild(tooltip);
          }
        }
      }, 200);
    }
  };

  useEffect(() => {
    let interval;
    if (isMobile !== true) {
      window.onmouseover = (e) => {
        const titledElement = getTitledElement(e.target);
        const isHoveringTooltip = isInsideTooltip(e.target) || isInsideTooltip(e.relatedTarget || {});
        if (titledElement) {
          if (state.current.element !== titledElement) {
            hideTooltip();
            state.current.element = titledElement;
            state.current.duration = new Date();
          }
        } else if (!isHoveringTooltip) {
          hideTooltip(e);
        }
      };

      window.onmouseout = (e) => {
        const isHoveringTooltip = isInsideTooltip(e.target) || isInsideTooltip(e.relatedTarget || {});
        if (!isHoveringTooltip) {
          hideTooltip(e);
        }
      };

      window.onmousedown = (e) => {
        const isHoveringTooltip = isInsideTooltip(e.target);
        if (!isHoveringTooltip) {
          hideTooltip();
        }
      };

      interval = setInterval(tooltipIntervalHandler, 60);
    }
    return () => clearInterval(interval);
  }, []);

  return null;
};

export default TooltipHandler;
